import { useState } from 'react';

declare function gtag(...args: unknown[]): void;

const FORM_URL = 'https://readdy.ai/api/form/d74aalv5hic0eqh31320';

interface ServiceContactSidebarProps {
  serviceName: string;
}

export default function ServiceContactSidebar({ serviceName }: ServiceContactSidebarProps) {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    fetch(FORM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
    })
      .then((res) => {
        if (res.ok) {
          if (typeof gtag === 'function') {
            gtag('event', 'conversion', {
              send_to: 'AW-17076629084/v2T0CLqaiZgcENzc4s4_',
            });
          }
          setSubmitted(true);
          form.reset();
        }
      })
      .catch(() => {
        // network error — do not fire conversion, do not show success
      });
  };

  return (
    <aside id="estimate" className="sticky top-28 space-y-5">
      {/* Call Now Box */}
      <div className="bg-gradient-to-br from-[#0a1628] to-[#162447] rounded-2xl p-6 text-center">
        <div className="bg-red-600 text-white text-xs font-black px-3 py-1 rounded-full inline-block mb-3 animate-pulse">
          24/7 EMERGENCY SERVICE
        </div>
        <p className="text-gray-300 text-sm mb-3 font-medium">
          Speak directly to a licensed specialist — not a call center
        </p>
        <a
          href="tel:7252675482"
          className="flex items-center justify-center gap-2 w-full bg-red-600 text-white px-5 py-5 rounded-xl font-black text-lg hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer mb-2"
        >
          <i className="ri-phone-fill text-2xl"></i>
          (725) 267-5482
        </a>
        <p className="text-gray-400 text-xs italic">Call now for same-day service</p>
        
        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-3 text-left">
          {[
            { icon: 'ri-shield-check-fill', label: 'Licensed & Insured' },
            { icon: 'ri-calendar-check-fill', label: 'Same-Day Service' },
            { icon: 'ri-money-dollar-circle-fill', label: 'Flat-Rate Pricing' },
            { icon: 'ri-chat-check-fill', label: 'Free Estimate' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${item.icon} text-cyan-400 text-base`}></i>
              </div>
              <span className="text-gray-300 text-xs font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Estimate Form */}
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <h3 className="text-xl font-black text-[#0f1f3d] mb-1">Get a Free Estimate</h3>
        <p className="text-gray-500 text-sm mb-4">No commitment — we'll respond same day.</p>

        {submitted ? (
          <div className="bg-green-50 border border-green-300 rounded-xl p-5 text-center">
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-3">
              <i className="ri-check-double-line text-green-600 text-2xl"></i>
            </div>
            <p className="font-bold text-green-800 text-base">Request Received!</p>
            <p className="text-green-700 text-sm mt-1">We'll be in touch shortly. For immediate help, call <a href="tel:7252675482" className="font-bold underline">(725) 267-5482</a>.</p>
          </div>
        ) : (
          <form
            id={`service-estimate-form-${serviceName.toLowerCase().replace(/\s+/g, '-')}`}
            data-readdy-form
            action={FORM_URL}
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-3"
          >
            <input type="hidden" name="service" value={serviceName} />
            <input
              type="text"
              name="name"
              placeholder="Your Name *"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number *"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <textarea
              name="message"
              placeholder="Describe your issue..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
            ></textarea>
            <button
              type="submit"
              className="w-full bg-[#0f1f3d] text-white px-5 py-4 rounded-lg font-bold text-sm hover:bg-[#162447] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Estimate
            </button>
            <p className="text-center text-xs text-gray-500">Same-day response • No obligation • Licensed &amp; Insured</p>
          </form>
        )}
      </div>

      {/* Google Rating */}
      <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <i key={s} className="ri-star-fill text-yellow-400 text-lg"></i>
            ))}
          </div>
          <div>
            <p className="font-black text-[#0f1f3d] text-sm">5.0 Rated on Google</p>
            <p className="text-gray-500 text-xs">22+ verified reviews</p>
          </div>
        </div>
      </div>
    </aside>
  );
}
